/**
 * NEXUMA — Permission System
 * Granular permission definitions for all HR SaaS modules.
 *
 * Permission format: module.action.scope
 *
 * Modules: dashboard, employees, contracts, attendance, leave,
 *          payroll, recruitment, performance, documents, reports,
 *          settings, audit_logs, roles_permissions
 *
 * Actions: view, create, edit, delete, approve, reject, export,
 *          manage, assign, lock, unlock
 *
 * Scopes: own, team, department, company, all
 */

// ── Module constants ──────────────────────────────────────────────────────────
export const MODULES = {
  DASHBOARD:         'dashboard',
  EMPLOYEES:         'employees',
  CONTRACTS:         'contracts',
  ATTENDANCE:        'attendance',
  LEAVE:             'leave',
  PAYROLL:           'payroll',
  RECRUITMENT:       'recruitment',
  ONBOARDING:        'onboarding',
  PERFORMANCE:       'performance',
  DOCUMENTS:         'documents',
  REPORTS:           'reports',
  SETTINGS:          'settings',
  AUDIT_LOGS:        'audit_logs',
  ROLES_PERMISSIONS: 'roles_permissions',
  COMMUNICATION:     'communication',
  LEAVE_ADJUSTMENTS: 'leave_adjustments',
  PROJECTS:          'projects',
  WARNINGS:          'warnings',
  ASSETS:            'assets',
  COMPLIANCE:        'compliance',
};

// ── Action constants ──────────────────────────────────────────────────────────
export const ACTIONS = {
  VIEW:    'view',
  CREATE:  'create',
  EDIT:    'edit',
  DELETE:  'delete',
  APPROVE: 'approve',
  REJECT:  'reject',
  EXPORT:  'export',
  MANAGE:  'manage',
  ASSIGN:  'assign',
  LOCK:    'lock',
  UNLOCK:  'unlock',
};

// ── Scope constants ───────────────────────────────────────────────────────────
export const SCOPES = {
  OWN:        'own',
  TEAM:       'team',
  DEPARTMENT: 'department',
  COMPANY:    'company',
  ALL:        'all',
};

/**
 * Build a permission string.
 * p('employees', 'view', 'own') → 'employees.view.own'
 * p('settings', 'manage') → 'settings.manage'
 */
export const p = (module, action, scope = null) =>
  scope ? `${module}.${action}.${scope}` : `${module}.${action}`;

// ── Full permission set ───────────────────────────────────────────────────────
// Organised per module for readability. Import individual perms from here.
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW_OWN:        p('dashboard', 'view', 'own'),
  DASHBOARD_VIEW_TEAM:       p('dashboard', 'view', 'team'),
  DASHBOARD_VIEW_DEPARTMENT: p('dashboard', 'view', 'department'),
  DASHBOARD_VIEW_COMPANY:    p('dashboard', 'view', 'company'),
  DASHBOARD_VIEW_ALL:        p('dashboard', 'view', 'all'),

  // Employees
  EMPLOYEES_VIEW_OWN:        p('employees', 'view', 'own'),
  EMPLOYEES_VIEW_TEAM:       p('employees', 'view', 'team'),
  EMPLOYEES_VIEW_DEPARTMENT: p('employees', 'view', 'department'),
  EMPLOYEES_VIEW_COMPANY:    p('employees', 'view', 'company'),
  EMPLOYEES_VIEW_ALL:        p('employees', 'view', 'all'),
  EMPLOYEES_CREATE:          p('employees', 'create'),
  EMPLOYEES_EDIT_OWN:        p('employees', 'edit', 'own'),
  EMPLOYEES_EDIT_DEPARTMENT: p('employees', 'edit', 'department'),
  EMPLOYEES_EDIT_COMPANY:    p('employees', 'edit', 'company'),
  EMPLOYEES_DELETE:          p('employees', 'delete'),
  EMPLOYEES_EXPORT:          p('employees', 'export'),
  EMPLOYEES_MANAGE:          p('employees', 'manage'),

  // Contracts
  CONTRACTS_VIEW_OWN:        p('contracts', 'view', 'own'),
  CONTRACTS_VIEW_DEPARTMENT: p('contracts', 'view', 'department'),
  CONTRACTS_VIEW_COMPANY:    p('contracts', 'view', 'company'),
  CONTRACTS_CREATE:          p('contracts', 'create'),
  CONTRACTS_EDIT:            p('contracts', 'edit'),
  CONTRACTS_DELETE:          p('contracts', 'delete'),
  CONTRACTS_APPROVE:         p('contracts', 'approve'),
  CONTRACTS_EXPORT:          p('contracts', 'export'),
  CONTRACTS_LOCK:            p('contracts', 'lock'),
  CONTRACTS_UNLOCK:          p('contracts', 'unlock'),

  // Attendance
  ATTENDANCE_VIEW_OWN:        p('attendance', 'view', 'own'),
  ATTENDANCE_VIEW_TEAM:       p('attendance', 'view', 'team'),
  ATTENDANCE_VIEW_DEPARTMENT: p('attendance', 'view', 'department'),
  ATTENDANCE_VIEW_COMPANY:    p('attendance', 'view', 'company'),
  ATTENDANCE_EDIT_OWN:        p('attendance', 'edit', 'own'),
  ATTENDANCE_EDIT_DEPARTMENT: p('attendance', 'edit', 'department'),
  ATTENDANCE_APPROVE:         p('attendance', 'approve'),
  ATTENDANCE_EXPORT:          p('attendance', 'export'),
  ATTENDANCE_MANAGE:          p('attendance', 'manage'),

  // Leave
  LEAVE_VIEW_OWN:             p('leave', 'view', 'own'),
  LEAVE_VIEW_TEAM:            p('leave', 'view', 'team'),
  LEAVE_VIEW_DEPARTMENT:      p('leave', 'view', 'department'),
  LEAVE_VIEW_COMPANY:         p('leave', 'view', 'company'),
  LEAVE_CREATE_OWN:           p('leave', 'create', 'own'),
  LEAVE_CREATE_COMPANY:       p('leave', 'create', 'company'),
  LEAVE_EDIT_OWN:             p('leave', 'edit', 'own'),
  LEAVE_EDIT_COMPANY:         p('leave', 'edit', 'company'),
  LEAVE_APPROVE:              p('leave', 'approve'),
  LEAVE_REJECT:               p('leave', 'reject'),
  LEAVE_EXPORT:               p('leave', 'export'),
  LEAVE_MANAGE:               p('leave', 'manage'),

  // Komunikimi — kanalet dhe mesazhet direkte (backend: routes/chatRoutes.js).
  // Deklaruar këtu që rolesConfig të pasqyrojë backend/config/rolePermissions.js;
  // asnjë zë sidebar-i apo route guard nuk varet ende prej tyre.
  COMMUNICATION_VIEW_OWN:     p('communication', 'view', 'own'),
  COMMUNICATION_VIEW_COMPANY: p('communication', 'view', 'company'),
  COMMUNICATION_CREATE_OWN:   p('communication', 'create', 'own'),
  COMMUNICATION_CREATE_COMPANY: p('communication', 'create', 'company'),
  COMMUNICATION_MANAGE:       p('communication', 'manage'),

  // Leave adjustments — "Rregullimet e pushimeve" (manual +/- of leave days).
  // Separate from leave.* on purpose: reading the ledger and being allowed to
  // move someone's balance are different levels of trust.
  LEAVE_ADJUSTMENTS_VIEW:     p('leave_adjustments', 'view'),
  LEAVE_ADJUSTMENTS_MANAGE:   p('leave_adjustments', 'manage'),

  // Payroll
  PAYROLL_VIEW_OWN:           p('payroll', 'view', 'own'),
  PAYROLL_VIEW_DEPARTMENT:    p('payroll', 'view', 'department'),
  PAYROLL_VIEW_COMPANY:       p('payroll', 'view', 'company'),
  PAYROLL_CREATE:             p('payroll', 'create'),
  PAYROLL_EDIT_DEPARTMENT:    p('payroll', 'edit', 'department'),
  PAYROLL_EDIT_COMPANY:       p('payroll', 'edit', 'company'),
  PAYROLL_APPROVE_COMPANY:    p('payroll', 'approve', 'company'),
  PAYROLL_EXPORT:             p('payroll', 'export'),
  PAYROLL_MANAGE:             p('payroll', 'manage'),
  PAYROLL_LOCK:               p('payroll', 'lock'),

  // Recruitment
  RECRUITMENT_VIEW:           p('recruitment', 'view'),
  RECRUITMENT_CREATE:         p('recruitment', 'create'),
  RECRUITMENT_EDIT:           p('recruitment', 'edit'),
  RECRUITMENT_DELETE:         p('recruitment', 'delete'),
  RECRUITMENT_APPROVE:        p('recruitment', 'approve'),
  RECRUITMENT_MANAGE:         p('recruitment', 'manage'),

  // ── Onboarding ──────────────────────────────────────────────────────────
  // Moduli i vet, jo nënfushë e rekrutimit: rekrutimi e NIS procesin, por
  // koordinimi, IT-ja, menaxheri dhe mbyllja i përkasin HR-së.
  ONBOARDING_VIEW:            p('onboarding', 'view'),
  ONBOARDING_CREATE:          p('onboarding', 'create'),
  ONBOARDING_EDIT:            p('onboarding', 'edit'),
  ONBOARDING_COMPLETE:        p('onboarding', 'complete'),
  ONBOARDING_MANAGE:          p('onboarding', 'manage'),

  // Performance
  PERFORMANCE_VIEW_OWN:       p('performance', 'view', 'own'),
  PERFORMANCE_VIEW_TEAM:      p('performance', 'view', 'team'),
  PERFORMANCE_VIEW_COMPANY:   p('performance', 'view', 'company'),
  PERFORMANCE_CREATE:         p('performance', 'create'),
  PERFORMANCE_EDIT:           p('performance', 'edit'),
  PERFORMANCE_APPROVE:        p('performance', 'approve'),
  // Autoriteti mbi vlerësimin e dikujt tjetër: vlerëso kë të duash, ndrysho
  // ose fshij edhe atë që e ka shkruar një koleg (backend/routes/evaluationRoutes.js).
  PERFORMANCE_MANAGE:         p('performance', 'manage'),

  // Documents
  DOCUMENTS_VIEW_OWN:         p('documents', 'view', 'own'),
  DOCUMENTS_VIEW_DEPARTMENT:  p('documents', 'view', 'department'),
  DOCUMENTS_VIEW_COMPANY:     p('documents', 'view', 'company'),
  DOCUMENTS_CREATE:           p('documents', 'create'),
  DOCUMENTS_EDIT:             p('documents', 'edit'),
  DOCUMENTS_DELETE:           p('documents', 'delete'),
  DOCUMENTS_EXPORT:           p('documents', 'export'),

  // Reports
  REPORTS_VIEW_OWN:           p('reports', 'view', 'own'),
  REPORTS_VIEW_DEPARTMENT:    p('reports', 'view', 'department'),
  REPORTS_VIEW_COMPANY:       p('reports', 'view', 'company'),
  REPORTS_VIEW_ALL:           p('reports', 'view', 'all'),
  REPORTS_EXPORT:             p('reports', 'export'),

  // Settings
  SETTINGS_VIEW:              p('settings', 'view'),
  SETTINGS_EDIT:              p('settings', 'edit'),
  SETTINGS_MANAGE:            p('settings', 'manage'),

  // Audit Logs
  AUDIT_LOGS_VIEW_COMPANY:    p('audit_logs', 'view', 'company'),
  AUDIT_LOGS_VIEW_ALL:        p('audit_logs', 'view', 'all'),
  AUDIT_LOGS_EXPORT:          p('audit_logs', 'export'),

  // Roles & Permissions
  ROLES_VIEW:                 p('roles_permissions', 'view'),
  ROLES_CREATE:               p('roles_permissions', 'create'),
  ROLES_EDIT:                 p('roles_permissions', 'edit'),
  ROLES_DELETE:               p('roles_permissions', 'delete'),
  ROLES_MANAGE:               p('roles_permissions', 'manage'),
  ROLES_ASSIGN:               p('roles_permissions', 'assign'),

  // Projects
  PROJECTS_VIEW_OWN:          p('projects', 'view', 'own'),
  PROJECTS_VIEW_TEAM:         p('projects', 'view', 'team'),
  PROJECTS_VIEW_COMPANY:      p('projects', 'view', 'company'),
  PROJECTS_CREATE:            p('projects', 'create'),
  PROJECTS_EDIT:              p('projects', 'edit'),
  PROJECTS_MANAGE:            p('projects', 'manage'),

  // Warnings
  WARNINGS_VIEW_OWN:          p('warnings', 'view', 'own'),
  WARNINGS_VIEW_DEPARTMENT:   p('warnings', 'view', 'department'),
  WARNINGS_VIEW_COMPANY:      p('warnings', 'view', 'company'),
  WARNINGS_CREATE:            p('warnings', 'create'),
  WARNINGS_EDIT:              p('warnings', 'edit'),
  WARNINGS_DELETE:            p('warnings', 'delete'),

  // Assets — regjistri i pajisjeve
  // `manage` është privilegji i ciklit: dorëzim dhe kthim. I ndarë nga `edit`
  // sepse janë dy gjëra të ndryshme — të ndryshosh të dhënat e një laptopi nuk
  // do të thotë të vendosësh se te kush shkon.
  ASSETS_VIEW_OWN:            p('assets', 'view', 'own'),
  ASSETS_VIEW_DEPARTMENT:     p('assets', 'view', 'department'),
  ASSETS_VIEW_COMPANY:        p('assets', 'view', 'company'),
  ASSETS_CREATE:              p('assets', 'create'),
  ASSETS_EDIT:                p('assets', 'edit'),
  ASSETS_DELETE:              p('assets', 'delete'),
  ASSETS_EXPORT:              p('assets', 'export'),
  ASSETS_MANAGE:              p('assets', 'manage'),

  // Compliance
  // Trajnimet (060) — lloji i financimit ndryshohet vetëm me TRAININGS_EDIT.
  TRAININGS_VIEW:             p('trainings', 'view'),
  TRAININGS_CREATE:           p('trainings', 'create'),
  TRAININGS_EDIT:             p('trainings', 'edit'),
  TRAININGS_DELETE:           p('trainings', 'delete'),
  TRAININGS_EXPORT:           p('trainings', 'export'),

  COMPLIANCE_VIEW:            p('compliance', 'view'),
  COMPLIANCE_MANAGE:          p('compliance', 'manage'),
};

export default PERMISSIONS;
